import { useEffect, useState } from "react";
import axios from "axios";
import { 
  Target,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronDown,
  Calendar,
  Award,
  Activity,
  BarChart3
} from "lucide-react";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, LineChart } from "recharts";

const ConditioningTestInsights = ({ userId, userTests }) => {
    const [selectedTest, setSelectedTest] = useState(null);
    const [testId, setTestId] = useState(userTests[0]?.test_id || '');
    const [insights, setInsights] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [showTable, setShowTable] = useState(false);

    useEffect(() => {
        if (testId && userId) {
            setIsLoading(true);
            setIsError(false);
            
            axios.get(`${process.env.REACT_APP_API_HOST}/api/insights/conditioning/tests/${userId}/${testId}`)
                .then(response => {
                    setInsights(response.data);
                    if (response.data?.exercise_progression?.length > 0) {
                        setSelectedExercise(response.data.exercise_progression[0]);
                    }
                    setIsLoading(false);
                })
                .catch(error => {
                    console.error('Error fetching conditioning test insights:', error);
                    setIsError(true);
                    setIsLoading(false);
                });
        }
    }, [userId, testId]);

    const handleTestChange = (event) => {
        const selectedId = event.target.value;
        setTestId(selectedId);
        const test = userTests.find(t => t.test_id === parseInt(selectedId));
        setSelectedTest(test || null);
    };

    const handleExerciseChange = (event) => {
        const exerciseId = parseInt(event.target.value);
        const exercise = insights?.exercise_progression?.find(e => e.test_exercise_id === exerciseId);
        setSelectedExercise(exercise || null);
    };

    const formatValue = (value, metric) => {
        if (value === null || value === undefined) return 'N/A';
        if (metric === 'Speed') return `${value.toFixed(2)}s`;
        if (metric === 'Max weight') return `${value.toFixed(1)}kg`;
        if (metric === 'Hold') return `${value.toFixed(1)}s`;
        return Math.round(value);
    };

    const getDirectionIcon = (direction) => {
        if (direction === 'improvement') return <TrendingUp className="text-green-600" size={20} />;
        if (direction === 'decline') return <TrendingDown className="text-red-600" size={20} />;
        return <Minus className="text-gray-600" size={20} />;
    };

    const getDirectionColor = (direction) => {
        if (direction === 'improvement') return 'bg-green-100 text-green-800';
        if (direction === 'decline') return 'bg-red-100 text-red-800';
        return 'bg-gray-100 text-gray-800';
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const value = payload[0].value;
            const formattedValue = selectedExercise ? formatValue(value, selectedExercise.metric) : value;

            return (
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3">
                    <p className="text-sm font-semibold text-gray-900 mb-2">
                        {new Date(label).toLocaleDateString()}
                    </p>
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-xs text-gray-600">Value:</span>
                        <span className="text-sm font-bold text-blue-600">
                            {formattedValue}
                        </span>
                    </div>
                </div>
            );
        }
        return null;
    };

    if (isError) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl p-6 shadow-lg text-center max-w-sm">
                    <p className="text-red-600 font-semibold">Error loading test insights</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-3 pb-6 new-entry">
            <div className="max-w-2xl mx-auto space-y-4">
                
                {/* Header */}
                <div>
                    <h2 className="text-sm text-gray-600 mt-0.5">
                        Conditioning Tests • Progress Tracking
                    </h2>
                </div>

                {/* Test Selector */}
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                    <label className="block text-lg font-semibold text-gray-700 mb-2">
                        Select Test
                    </label>
                    <div className="relative">
                        <select
                            value={testId || ''}
                            onChange={handleTestChange}
                            className="w-full appearance-none bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 pr-10 text-gray-800 font-medium focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                            style={{ fontSize: '1rem' }}
                        >
                            {userTests.map((test) => (
                                <option key={test.test_id} value={test.test_id} className="text-base">
                                    {test.title}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={20} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                    </div>
                </div>

                {isLoading ? (
                    <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="text-gray-600 text-lg mt-4">Loading insights...</p>
                    </div>
                ) : insights ? (
                    <>
                        {/* Latest Session Summary */}
                        {insights.latest_session && (
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 shadow-sm border border-blue-200">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="bg-blue-600 rounded-lg p-1.5">
                                        <Calendar className="text-white" size={16} />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900">Latest Test Session</h3>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <p className="text-lg text-gray-600 mb-1">Date</p>
                                        <p className="text-base font-semibold text-gray-900">
                                            {new Date(insights.latest_session.test_date).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-lg text-gray-600 mb-1">Completion</p>
                                        <p className="text-base font-semibold text-gray-900">
                                            {insights.latest_session.exercises_completed} / {insights.latest_session.total_exercises} exercises
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Overall Progress */}
                        {insights.overall_progress && (
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="bg-purple-100 rounded-lg p-1.5">
                                        <Award className="text-purple-600" size={16} />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900">Overall Progress</h3>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <div className="bg-gray-50 rounded-lg p-3">
                                        <div className="flex items-center justify-between mb-1">
                                            <p className="text-lg text-gray-600">Change vs Previous</p>
                                            {getDirectionIcon(insights.overall_progress.direction)}
                                        </div>
                                        <p className="text-xl font-bold text-gray-900">
                                            {insights.overall_progress.percentage_change > 0 ? '+' : ''}
                                            {insights.overall_progress.percentage_change.toFixed(1)}%
                                        </p>
                                        <p className="text-lg text-gray-500 mt-1">
                                            vs {new Date(insights.overall_progress.comparison_date).toLocaleDateString()}
                                        </p>
                                    </div>
                                    
                                    <div className="bg-gray-50 rounded-lg p-3">
                                        <p className="text-lg text-gray-600 mb-1">Status</p>
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xl font-semibold ${getDirectionColor(insights.overall_progress.direction)}`}>
                                            {insights.overall_progress.direction.charAt(0).toUpperCase() + insights.overall_progress.direction.slice(1)}
                                        </span>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-2">
                                    <div className="text-center bg-green-50 rounded-lg p-2">
                                        <p className="text-lg font-bold text-green-700">
                                            {insights.overall_progress.exercises_improved}
                                        </p>
                                        <p className="text-base text-gray-600">Improved</p>
                                    </div>
                                    <div className="text-center bg-gray-50 rounded-lg p-2">
                                        <p className="text-lg font-bold text-gray-700">
                                            {insights.overall_progress.exercises_stable}
                                        </p>
                                        <p className="text-base text-gray-600">Stable</p>
                                    </div>
                                    <div className="text-center bg-red-50 rounded-lg p-2">
                                        <p className="text-lg font-bold text-red-700">
                                            {insights.overall_progress.exercises_declined}
                                        </p>
                                        <p className="text-base text-gray-600">Declined</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Exercise Progression */}
                        {insights.exercise_progression && insights.exercise_progression.length > 0 && (
                            <>
                                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                    <label className="block text-base font-semibold text-gray-700 mb-2">
                                        Select Exercise
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={selectedExercise?.test_exercise_id || ''}
                                            onChange={handleExerciseChange}
                                            className="w-full appearance-none bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 pr-10 text-gray-800 font-medium focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                                            style={{ fontSize: '1rem' }}
                                        >
                                            {insights.exercise_progression.map((exercise) => (
                                                <option key={exercise.test_exercise_id} value={exercise.test_exercise_id} className="text-base">
                                                    {exercise.exercise_name} ({exercise.metric})
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown size={20} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                                    </div>
                                    
                                    {selectedExercise && <>
                                        {/* Exercise Details */}
                                        {selectedExercise.details && (
                                            <div className="bg-blue-50 rounded-xl p-4 shadow-sm border border-blue-200 mt-4">
                                                <p className="text-lg text-gray-700">
                                                    <span className="font-semibold">Details:</span> {selectedExercise.details}
                                                </p>
                                            </div>
                                        )}
                                    </>}
                                </div>

                                {selectedExercise && (
                                    <>
                                        {/* Exercise Stats Cards */}
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="bg-blue-100 rounded-lg p-1.5">
                                                        <Activity className="text-blue-600" size={16} />
                                                    </div>
                                                    <p className="text-xl text-gray-600">Current</p>
                                                </div>
                                                <p className="text-2xl font-bold text-gray-900">
                                                    {formatValue(selectedExercise.current_value, selectedExercise.metric)}
                                                </p>
                                            </div>

                                            {selectedExercise.percentage_change !== null && (
                                                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <div className="bg-purple-100 rounded-lg p-1.5">
                                                            <BarChart3 className="text-purple-600" size={16} />
                                                        </div>
                                                        <p className="text-xl text-gray-600">Change</p>
                                                    </div>
                                                    <p className={`text-2xl font-bold ${selectedExercise.percentage_change > 0 ? 'text-green-600' : selectedExercise.percentage_change < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                                        {selectedExercise.percentage_change > 0 ? '+' : ''}
                                                        {selectedExercise.percentage_change.toFixed(1)}%
                                                    </p>
                                                </div>
                                            )}

                                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="bg-green-100 rounded-lg p-1.5">
                                                        <Award className="text-green-600" size={16} />
                                                    </div>
                                                    <p className="text-xl text-gray-600">Best</p>
                                                </div>
                                                <p className="text-2xl font-bold text-gray-900">
                                                    {formatValue(selectedExercise.best_value, selectedExercise.metric)}
                                                </p>
                                            </div>

                                            {selectedExercise.previous_value !== null && (
                                                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <div className="bg-orange-100 rounded-lg p-1.5">
                                                            <Target className="text-orange-600" size={16} />
                                                        </div>
                                                        <p className="text-xl text-gray-600">Previous</p>
                                                    </div>
                                                    <p className="text-2xl font-bold text-gray-900">
                                                        {formatValue(selectedExercise.previous_value, selectedExercise.metric)}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Chart */}
                                        {selectedExercise.data_points && selectedExercise.data_points.length > 1 && (
                                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                                                <h3 className="text-lg font-bold text-gray-900 mb-4">
                                                    Progress Over Time
                                                </h3>
                                                
                                                <ResponsiveContainer width="100%" height={250}>
                                                    <LineChart data={[...selectedExercise.data_points].reverse()}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                        <XAxis 
                                                            dataKey="test_date" 
                                                            tick={{ fontSize: 11 }}
                                                            stroke="#9ca3af"
                                                            tickFormatter={(date) => new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                                        />
                                                        <YAxis 
                                                            tick={{ fontSize: 11 }}
                                                            stroke="#9ca3af"
                                                        />
                                                        <Tooltip content={<CustomTooltip />} />
                                                        <Line 
                                                            type="monotone" 
                                                            dataKey="value" 
                                                            stroke="#3b82f6"
                                                            strokeWidth={3}
                                                            dot={{ fill: '#3b82f6', r: 4 }}
                                                            activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Data Table Toggle */}
                                <button
                                    onClick={() => setShowTable(!showTable)}
                                    className="w-full bg-white rounded-xl p-4 shadow-sm border border-gray-200 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                >
                                    <span className="text-lg font-semibold text-gray-900">
                                        {showTable ? 'Hide' : 'Show'} All Exercises Summary
                                    </span>
                                    <ChevronDown 
                                        size={20} 
                                        className={`text-gray-600 transition-transform ${showTable ? 'rotate-180' : ''}`}
                                    />
                                </button>

                                {/* All Exercises Table */}
                                {showTable && (
                                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        <th className="px-3 py-3 text-left text-base font-semibold text-gray-600">Exercise</th>
                                                        <th className="px-3 py-3 text-right text-base font-semibold text-gray-600">Current</th>
                                                        <th className="px-3 py-3 text-right text-base font-semibold text-gray-600">Previous</th>
                                                        <th className="px-3 py-3 text-right text-base font-semibold text-gray-600">Change</th>
                                                        <th className="px-3 py-3 text-right text-base font-semibold text-gray-600">Best</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200">
                                                    {insights.exercise_progression.map((exercise) => (
                                                        <tr key={exercise.test_exercise_id} className="hover:bg-gray-50">
                                                            <td className="px-3 py-3 text-sm font-medium text-gray-900">
                                                                {exercise.exercise_name}
                                                                <span className="text-gray-500 ml-1">({exercise.metric})</span>
                                                            </td>
                                                            <td className="px-3 py-3 text-sm text-gray-600 text-right">
                                                                {formatValue(exercise.current_value, exercise.metric)}
                                                            </td>
                                                            <td className="px-3 py-3 text-sm text-gray-600 text-right">
                                                                {formatValue(exercise.previous_value, exercise.metric)}
                                                            </td>
                                                            <td className="px-3 py-3 text-sm text-right">
                                                                {exercise.percentage_change !== null ? (
                                                                    <span className={`font-semibold ${exercise.percentage_change > 0 ? 'text-green-600' : exercise.percentage_change < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                                                        {exercise.percentage_change > 0 ? '+' : ''}{exercise.percentage_change.toFixed(1)}%
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-gray-400">-</span>
                                                                )}
                                                            </td>
                                                            <td className="px-3 py-3 text-sm text-gray-600 text-right font-semibold">
                                                                {formatValue(exercise.best_value, exercise.metric)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {(!insights.exercise_progression || insights.exercise_progression.length === 0) && (
                            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center">
                                <Target className="text-gray-400 mx-auto mb-3" size={48} />
                                <p className="text-gray-600">No exercise data available for this test yet.</p>
                                <p className="text-sm text-gray-500 mt-2">Complete at least one test session to see insights.</p>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center">
                        <Target className="text-gray-400 mx-auto mb-3" size={48} />
                        <p className="text-gray-600">Select a test to view insights.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConditioningTestInsights;